import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useT } from "../../../i18n/useT";
import { getCurrentLocale } from "../../../utlis/locale";
import { dateFormatHanlder } from "../../../utlis/commonFunction";
import { isMobilityReportStale } from "../../mmtUserList/reviewState";
import { getReviewHistory } from "../../../services/dynamicActionPlanApi";
import "./mobilityOverview.scss";

// The share chain (summary sheet → share window) lives with the lazily loaded
// action plan; load it only when a past review is shared.
const PatientSummaryShare = lazy(
  () => import("./dynamicActionPlanV3/PatientSummaryShare"),
);

/**
 * Metrics tab overview — the DAP87 patient-page layout, for every customer
 * type:
 *
 *   status card (only when a new Mobility Check is needed)
 *   → action plan (host-rendered DynamicActionPlan)
 *   → See Mobility History
 *   → Previous Actions (saved reviews; hidden when there are none). Pharmacy
 *     rows can preview and share that review's Patient Summary.
 *
 * "Take Mobility Assessment" opens the existing test picker (`onTakeAssessment`).
 */

const ChartIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="26"
    height="26"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 4v16h16"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18 9l-5 5-3-3-3 3"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <path d="M16 6l-4-4-4 4M12 2v13" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M9 18l6-6-6-6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NotesIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6M8 13h8M8 17h5" />
  </svg>
);

/** Backend review record (§7) or a just-saved `onSaved` review → one row. */
export const toActionRow = (review) => ({
  id: review.reviewId || review.id,
  date: review.reviewedAt || review.savedAt,
  reviewer:
    review.reviewedBy?.displayName || review.reviewer || review.reviewerLabel,
  outcome:
    review.selectedPathway?.label || review.outcomeLabel || review.outcome,
  pathwayId: review.selectedPathway?.id || review.outcomeId,
  assessmentId: review.assessmentId,
  assessmentDate: review.assessmentDate,
  recommendationText: review.recommendationText,
  notes: (review.notes || [])
    .map((note) => (typeof note === "string" ? note : note?.text))
    .filter(Boolean),
});

/**
 * The plan payload re-targeted at one saved review, for the Patient Summary.
 * The payload's summary describes the latest check and the last saved
 * pathway; a review of another pathway gets its own next-step copy (or an
 * empty, editable one), and a review of an older check gets that check's date.
 */
export const reviewSharePlan = (plan, row, { checkLine } = {}) => {
  const summary = plan.summary || {};
  const lastPathwayId = (plan.checklist?.items || []).find(
    (item) => item.completed,
  )?.id;
  const nextStepText =
    row.recommendationText ??
    (row.pathwayId && row.pathwayId === lastPathwayId
      ? summary.recommendation?.text
      : "");
  const sameCheck =
    !row.assessmentId ||
    !plan.assessment?.id ||
    row.assessmentId === plan.assessment.id;
  const sections =
    sameCheck || !checkLine
      ? summary.sections
      : [
          {
            label: summary.sections?.[0]?.label,
            kind: "lines",
            lines: [{ text: checkLine }],
          },
        ];
  return {
    response: { ...plan, summary: { ...summary, sections } },
    nextStepText,
  };
};

const MobilityOverview = ({
  seniorId,
  tenantType,
  hasReport,
  reportDocument,
  reportDate,
  renderPlan,
  onViewHistory,
  onTakeAssessment,
}) => {
  const t = useT("personDashboard");
  const tPlan = useT("actionPlan");
  const locale = getCurrentLocale();
  const [actions, setActions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [shareMessage, setShareMessage] = useState("");

  useEffect(() => {
    if (!seniorId) {
      setActions([]);
      return undefined;
    }
    let active = true;
    getReviewHistory(seniorId, { tenant: tenantType })
      .then((res) => {
        if (active) setActions((res?.items || []).map(toActionRow));
      })
      .catch(() => {
        // Not every tenant exposes review history yet; the card stays hidden.
        if (active) setActions([]);
      });
    return () => {
      active = false;
    };
  }, [seniorId, tenantType]);

  const handleSaved = useCallback((review) => {
    const row = toActionRow(review);
    setActions((current) => [
      row,
      ...current.filter((existing) => existing.id !== row.id),
    ]);
  }, []);

  const formatDate = (date) => (date ? dateFormatHanlder(date, locale) : "");
  const canShare =
    tenantType === "pharmacy" &&
    !!plan?.summary &&
    plan.assessment?.current !== false;
  const patientId = (plan?.patient?.details || []).find(
    (detail) =>
      /\bID$/.test(detail.label || "") && detail.label !== "SeniorLife ID",
  );
  const shareTarget = sharing
    ? reviewSharePlan(plan, sharing, {
        checkLine: sharing.assessmentDate
          ? `${tPlan("mobility_check_label")} \u00b7 ${formatDate(
              sharing.assessmentDate,
            )}`
          : "",
      })
    : null;
  // v2 engine passes the render document; the v95 engine passes the raw date.
  const reportDateLabel = reportDocument
    ? reportDocument.partner_summary?.assessment_date_label ||
      reportDocument.assessment?.date_label ||
      reportDocument.assessment?.last_assessment_date_label ||
      ""
    : reportDate
      ? formatDate(reportDate)
      : "";
  const stale =
    hasReport &&
    (reportDocument
      ? reportDocument.freshness?.outdated === true
      : isMobilityReportStale(reportDate) === true);
  const needsAssessment = !hasReport || stale;

  return (
    <div className="mobility-overview">
      {needsAssessment ? (
        <div
          className="mobility-overview-card"
          data-state={stale ? "stale" : "no-check"}
        >
          <p className="mobility-overview-title">
            {t("new_assessment_needed")}
          </p>
          <p className="mobility-overview-desc">
            {stale
              ? reportDocument
                ? reportDocument.freshness.notice ||
                  reportDocument.freshness.label
                : t("last_check_over_30_days", { date: formatDate(reportDate) })
              : t("no_mobility_check_yet")}
          </p>
          <button
            type="button"
            className="mobility-overview-cta"
            onClick={onTakeAssessment}
          >
            {t("take_mobility_assessment")}
          </button>
        </div>
      ) : null}

      {shareMessage ? (
        <div className="mobility-overview-share-message" role="status">
          {shareMessage}
        </div>
      ) : null}

      {renderPlan({ onSaved: handleSaved, onLoaded: setPlan })}

      <button
        type="button"
        className="mobility-overview-history"
        onClick={onViewHistory}
        disabled={!hasReport}
      >
        <span className="mobility-overview-history-icon">
          <ChartIcon />
        </span>
        <span className="mobility-overview-history-copy">
          <strong>{t("see_mobility_history")}</strong>
          <span>
            {hasReport
              ? reportDateLabel
                ? `${t("last_tested")} ${reportDateLabel}`
                : "\u00a0"
              : t("never_tested")}
          </span>
        </span>
        <span className="mobility-overview-forward">
          <ChevronIcon />
        </span>
      </button>

      {!needsAssessment ? (
        <button
          type="button"
          className="mobility-overview-secondary"
          onClick={onTakeAssessment}
        >
          {t("take_mobility_assessment")}
        </button>
      ) : null}

      {actions.length ? (
        <div className="mobility-overview-actions" data-previous-actions>
          <div className="mobility-overview-actions-head">
            <NotesIcon />
            <strong>
              {tenantType === "pharmacy"
                ? t("previous_pharmacist_actions")
                : t("previous_actions")}
            </strong>
            <span>{actions.length}</span>
          </div>
          <ul className="mobility-overview-action-list">
            {actions.map((action) => (
              <li key={action.id}>
                <small>
                  {formatDate(action.date)}
                  {action.reviewer ? (
                    <>
                      {" · "}
                      <span className="notranslate">{action.reviewer}</span>
                    </>
                  ) : null}
                </small>
                {action.outcome ? <b>{action.outcome}</b> : null}
                {action.notes.map((note, index) => (
                  <q key={index}>{note}</q>
                ))}
                {canShare ? (
                  <button
                    type="button"
                    className="mobility-overview-action-share"
                    onClick={() => {
                      setShareMessage("");
                      setSharing(action);
                    }}
                  >
                    <ShareIcon />
                    {t("preview_and_share")}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {shareTarget ? (
        <Suspense fallback={null}>
          <PatientSummaryShare
            response={shareTarget.response}
            seniorId={seniorId || ""}
            review={{ notes: sharing.notes }}
            externalId={patientId?.value}
            discussedText={sharing.outcome || undefined}
            nextStepText={shareTarget.nextStepText}
            onClose={() => setSharing(null)}
            onShared={(channel) => {
              setSharing(null);
              setShareMessage(
                channel === "share"
                  ? tPlan("share_success")
                  : tPlan("share_email_opened"),
              );
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
};

export default MobilityOverview;
